import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { routes, type ProductPhotoCutoutJob, type ProductPhotos } from "@nova-ai/shared"

import { apiFetch } from "../lib/api"
import { queryKeys } from "./query-keys"

type PhotoIdentity = { simpleSku: string; sourceKey: string }
const CUTOUT_JOB_POLL_INTERVAL_MS = 1_000

function resultValue<T>(result: Awaited<ReturnType<typeof apiFetch<T>>>): T {
  if (!result.ok) {
    throw new Error(result.error.message)
  }
  return result.value
}

export function useProductPhotos(configSku: string, store: string) {
  const queryClient = useQueryClient()
  const queryKey = queryKeys.productPhotos(configSku, store)
  const query = useQuery({
    queryKey,
    enabled: configSku.length > 0 && store.length > 0,
    refetchInterval: (current) =>
      hasActiveCutoutJobs(current.state.data) ? CUTOUT_JOB_POLL_INTERVAL_MS : false,
    queryFn: async () =>
      resultValue(
        await apiFetch<ProductPhotos>(routes.products.productPhotos({ sku: configSku }), {
          query: { store },
        })
      ),
  })
  const refresh = async () => queryClient.invalidateQueries({ queryKey })
  const assign = useMutation({
    mutationFn: async ({ simpleSku, sourceKey }: PhotoIdentity) =>
      resultValue(
        await apiFetch(routes.productVisuals.photoAssignments({ store, simpleSku }), {
          method: "POST",
          body: { sourceKey },
        })
      ),
    onSuccess: refresh,
  })
  const generate = useMutation({
    mutationFn: async ({ simpleSku, sourceKey }: PhotoIdentity) => {
      const started = resultValue(
        await apiFetch<ProductPhotoCutoutJob>(routes.productVisuals.cutout({ store, simpleSku }), {
          method: "POST",
          body: { sourceKey },
        })
      )
      return started
    },
    onSuccess: refresh,
  })

  return { ...query, assign, generate }
}

function hasActiveCutoutJobs(photos: ProductPhotos | undefined): boolean {
  return Boolean(
    photos?.simples.some((simple) =>
      simple.sources.some(
        (source) => source.latestJob?.status === "queued" || source.latestJob?.status === "running"
      )
    )
  )
}
